import React, { useState, useEffect, useCallback } from 'react';
import { Download, Upload, FileText, CheckCircle, AlertCircle, Mail, Shield, ArrowLeft, ArrowRight, RefreshCw, Eye, X, Send, Sparkles, Smartphone } from 'lucide-react';

const ResumeImproverApp = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [pdfFile, setPdfFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [result, setResult] = useState(null);
  
  // OTP Flow States
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [email, setEmail] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpResendCountdown, setOtpResendCountdown] = useState(0);
  
  // Enhanced UI States
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDragOver, setIsDragOver] = useState(false);
  const [apiError, setApiError] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Environment variables
  const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:9000';
  const MAX_FILE_SIZE = import.meta.env.VITE_MAX_FILE_SIZE || 10485760;
  const APP_VERSION = import.meta.env.VITE_APP_VERSION || '1.0.0';
  const ENVIRONMENT = import.meta.env.VITE_APP_ENVIRONMENT || 'development';
  const ENABLE_DEMO_MODE = import.meta.env.VITE_ENABLE_DEMO_MODE === 'true';
  const IS_DEV = import.meta.env.DEV || ENVIRONMENT === 'development';

  const questions = [
    {
      id: "target_role",
      question: "What specific role or position are you targeting?",
      required: true,
      type: "text",
      placeholder: "e.g., Senior Software Engineer, Marketing Manager",
      icon: "🎯"
    },
    {
      id: "industry",
      question: "What industry are you applying to?",
      required: true,
      type: "text",
      placeholder: "e.g., Technology, Healthcare, Finance",
      icon: "🏢"
    },
    {
      id: "experience_level",
      question: "What is your experience level?",
      required: true,
      type: "select",
      options: ["Entry-level", "Mid-level", "Senior", "Executive"],
      icon: "📈"
    },
    {
      id: "key_skills",
      question: "What are your top 3-5 key skills you want to highlight?",
      required: true,
      type: "textarea",
      placeholder: "e.g., Python, AWS, Machine Learning, Leadership, Project Management",
      icon: "⚡"
    },
    {
      id: "achievements",
      question: "What are your most significant achievements or accomplishments?",
      required: false,
      type: "textarea",
      placeholder: "e.g., Led team of 10, increased revenue by 25%, launched 3 successful products",
      icon: "🏆"
    },
    {
      id: "career_gap",
      question: "Do you have any career gaps or transitions you'd like help addressing?",
      required: false,
      type: "textarea",
      placeholder: "Optional: Any career changes or gaps to address",
      icon: "🔄"
    },
    {
      id: "format_preference",
      question: "Do you prefer a modern, traditional, or creative resume format?",
      required: false,
      type: "select",
      options: ["Modern", "Traditional", "Creative"],
      icon: "🎨"
    }
  ];

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // OTP resend countdown
  useEffect(() => {
    let interval = null;
    if (otpResendCountdown > 0) {
      interval = setInterval(() => {
        setOtpResendCountdown(countdown => countdown - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpResendCountdown]);

  // Development logging
  useEffect(() => {
    if (IS_DEV) {
      console.log('🚀 Resume Improver App started');
      console.log('📡 API Base URL:', API_BASE_URL);
      console.log('📝 App Version:', APP_VERSION);
      console.log('🔧 Environment:', ENVIRONMENT);
      console.log('🎭 Demo Mode:', ENABLE_DEMO_MODE ? 'Enabled' : 'Disabled');
      console.log('📱 Mobile Device:', isMobile);
    }
  }, [isMobile]);

  const validateEmail = (email) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateForm = useCallback(() => {
    const newErrors = {};
    const requiredFields = questions.filter(q => q.required).map(q => q.id);
    
    requiredFields.forEach(field => {
      if (!formData[field]?.trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleInputChange = (questionId, value) => {
    setFormData(prev => ({ ...prev, [questionId]: value }));
    if (errors[questionId]) {
      setErrors(prev => ({ ...prev, [questionId]: '' }));
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    processFile(file);
  };

  const processFile = (file) => {
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please select a valid PDF file');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      alert(`File size must be less than ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB`);
      return;
    }

    setPdfFile(file);
    
    if (IS_DEV) {
      console.log('📄 File selected:', file.name, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files[0];
    processFile(file);
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Helper function to convert base64 to Blob
  const base64ToBlob = (base64Data, contentType) => {
    try {
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      return new Blob([byteArray], { type: contentType });
    } catch (error) {
      console.error('Error converting base64 to blob:', error);
      return null;
    }
  };

  // Enhanced progress simulation
  const simulateProgress = () => {
    const steps = [
      { progress: 10, text: "Uploading your resume..." },
      { progress: 25, text: "Parsing PDF content..." },
      { progress: 40, text: "Analyzing current structure..." },
      { progress: 60, text: "ChatGPT is enhancing your resume..." },
      { progress: 80, text: "Compiling LaTeX to PDF..." },
      { progress: 95, text: "Finalizing improvements..." },
      { progress: 100, text: "Complete!" }
    ];

    steps.forEach((step, index) => {
      setTimeout(() => {
        setProgress(step.progress);
        setProgressText(step.text);
        
        if (IS_DEV) {
          console.log(`⏳ ${step.progress}%: ${step.text}`);
        }
      }, index * 1000);
    });
  };

  // Enhanced demo result generator
  const generateDemoResult = () => ({
    improved_resume_pdf: null, // No PDF in demo mode
    improved_resume_text: `IMPROVED RESUME - ${formData.target_role || 'Professional'} [DEMO]

PROFESSIONAL SUMMARY
Experienced ${formData.experience_level?.toLowerCase() || 'professional'} in ${formData.industry || 'various industries'} with a proven track record of delivering exceptional results. Skilled in ${formData.key_skills || 'multiple areas'} with a passion for driving innovation and achieving organizational goals.

KEY SKILLS
• ${formData.key_skills?.split(',').map(skill => skill.trim()).join('\n• ') || 'Various technical and soft skills'}

PROFESSIONAL EXPERIENCE
Senior Professional | Company Name | 2020 - Present
• Led cross-functional teams to deliver high-impact projects
• ${formData.achievements || 'Achieved significant business results through strategic initiatives'}
• Implemented process improvements resulting in 25% efficiency increase
• Collaborated with stakeholders to drive organizational success

EDUCATION
Bachelor's Degree in Relevant Field
University Name | Year

---
⚠️ DEMO VERSION - This is generated demo content.
In production, this would be your actual improved resume generated by ChatGPT and formatted as a professional PDF using LaTeX.

Environment: ${ENVIRONMENT}
Generated: ${new Date().toLocaleString()}
Version: ${APP_VERSION}`,
    format: 'text',
    original_length: 1234,
    improved_length: 1567,
    analytics: {
      improvements: {
        action_verb_increase: 12,
        quantification_increase: 8
      }
    }
  });

  const handleSubmit = async () => {
    if (!validateForm() || !pdfFile) {
      alert('Please fill in all required fields and upload your resume');
      return;
    }

    setIsProcessing(true);
    setCurrentStep(3);
    setApiError('');
    simulateProgress();

    try {
      const pdfBase64 = await fileToBase64(pdfFile);
      
      if (IS_DEV) {
        console.log('🚀 Sending request to:', `${API_BASE_URL}/improve-resume`);
        console.log('📋 Form data:', formData);
      }
      
      const response = await fetch(`${API_BASE_URL}/improve-resume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          resume_pdf: pdfBase64,
          answers: formData
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: `HTTP ${response.status}` }));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setResult(result);
      
      if (IS_DEV) {
        console.log('✅ API Response received:', result);
      }
      
      setTimeout(() => {
        setCurrentStep(4);
        setIsProcessing(false);
      }, 1000);

    } catch (error) {
      console.error('❌ API Error:', error);
      
      if (ENABLE_DEMO_MODE || IS_DEV) {
        setApiError(`Demo Mode: ${error.message}`);
        
        setTimeout(() => {
          setResult(generateDemoResult());
          setProgress(100);
          setProgressText("Demo Complete!");
          setCurrentStep(4);
          setIsProcessing(false);
        }, 3000);
      } else {
        setApiError(error.message);
        setIsProcessing(false);
        setCurrentStep(2);
        alert(`Error: ${error.message}`);
      }
    }
  };

  const handleDownload = () => {
    setShowEmailModal(true);
  };

  // OTP Functions
  const sendOtpToEmail = async () => {
    if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setEmailError('');
    setIsOtpLoading(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/send-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many requests. Please wait 1 minute before trying again.');
        }
        throw new Error(data.error || 'Failed to send OTP');
      }

      console.log('OTP sent successfully:', data);
      setShowEmailModal(false);
      setShowOtpModal(true);
      setOtpResendCountdown(60);

    } catch (error) {
      console.error('OTP Error:', error);
      
      if (error.message.includes('rate limit') || error.message.includes('Too many')) {
        setEmailError('Too many requests. Please wait 1 minute and try again.');
      } else if (error.message.includes('Email verification')) {
        setEmailError('Email verification required. Please check your AWS SES setup.');
      } else {
        setEmailError(error.message);
      }
    } finally {
      setIsOtpLoading(false);
    }
  };

  const verifyOtpAndSendEmail = async () => {
    if (!otpCode.trim()) {
      setOtpError('Please enter the OTP code');
      return;
    }

    if (otpCode.length !== 6) {
      setOtpError('OTP code must be 6 digits');
      return;
    }

    setOtpError('');
    setIsVerifyingOtp(true);

    try {
      const response = await fetch(`${API_BASE_URL}/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp_code: otpCode.trim(),
          resume_pdf_base64: result.improved_resume_pdf || ''
        })
      });

      const data = await response.json();

      if (!response.ok) {
        if (response.status === 429) {
          throw new Error('Too many verification attempts. Please request a new OTP.');
        }
        throw new Error(data.error || 'OTP verification failed');
      }

      setShowOtpModal(false);
      setOtpCode('');
      setEmail('');
      
      alert('🎉 Success! Your improved resume has been sent to your email address. Please check your inbox (and spam folder).');

    } catch (error) {
      console.error('OTP Verification Error:', error);
      
      if (error.message.includes('Invalid OTP') || error.message.includes('CodeMismatchException')) {
        setOtpError('Invalid OTP code. Please check the code and try again.');
      } else if (error.message.includes('expired') || error.message.includes('ExpiredCodeException')) {
        setOtpError('OTP code has expired. Please request a new code.');
      } else if (error.message.includes('Too many')) {
        setOtpError('Too many attempts. Please request a new OTP.');
      } else {
        setOtpError('Verification failed. Please try again or request a new code.');
      }
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const resetApp = () => {
    setCurrentStep(1);
    setFormData({});
    setPdfFile(null);
    setIsProcessing(false);
    setProgress(0);
    setProgressText('');
    setResult(null);
    setShowEmailModal(false);
    setShowOtpModal(false);
    setShowPreviewModal(false);
    setEmail('');
    setOtpCode('');
    setEmailError('');
    setOtpError('');
    setErrors({});
    setApiError('');
    setOtpResendCountdown(0);
    
    if (IS_DEV) {
      console.log('🔄 App reset');
    }
  };

  const nextStep = () => {
    if (currentStep === 1 && !pdfFile) {
      alert('Please upload your resume first');
      return;
    }
    if (currentStep === 2 && !validateForm()) {
      alert('Please fill in all required fields');
      return;
    }
    setCurrentStep(prev => Math.min(prev + 1, 4));
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  return (
    <>
      {/* Enhanced CSS */}
      <style>{`
        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        
        @keyframes checkmark {
          0% { transform: scale(0); opacity: 0; }
          50% { transform: scale(1.2); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .float-animation {
          animation: float 3s ease-in-out infinite;
        }
        
        .checkmark {
          animation: checkmark 0.6s ease-in-out;
        }
        
        .spinner-custom {
          width: 60px;
          height: 60px;
          border: 4px solid rgba(102, 126, 234, 0.3);
          border-left: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .file-upload-area {
          border: 2px dashed #dee2e6;
          border-radius: 15px;
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .file-upload-area:hover,
        .file-upload-area.dragover {
          border-color: #667eea;
          background-color: #f8f9ff;
          transform: translateY(-2px);
        }

        .question-card {
          transition: all 0.3s ease;
          border-radius: 15px !important;
        }

        .question-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.1);
        }

        .btn-gradient {
          background: linear-gradient(45deg, #667eea, #764ba2);
          border: none;
          color: white;
        }

        .btn-gradient:hover {
          background: linear-gradient(45deg, #5a6fd8, #6a4190);
          transform: translateY(-1px);
          color: white;
        }

        .progress-enhanced {
          border-radius: 10px;
          box-shadow: inset 0 2px 4px rgba(0,0,0,0.1);
        }

        .progress-bar-custom {
          background: linear-gradient(45deg, #667eea, #764ba2);
          border-radius: 10px;
        }

        .form-control-enhanced,
        .form-select {
          border-radius: 12px;
          border: 2px solid #e9ecef;
          transition: all 0.3s ease;
        }

        .form-control-enhanced:focus,
        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
        }

        /* Mobile optimizations */
        @media (max-width: 768px) {
          .container {
            padding-left: 15px !important;
            padding-right: 15px !important;
          }
          
          .card-body {
            padding: 20px !important;
          }
          
          .btn-lg {
            padding: 12px 20px;
            font-size: 16px;
          }
          
          .step-circle {
            width: 40px !important;
            height: 40px !important;
            font-size: 14px;
          }
          
          .step-line {
            width: 30px !important;
            margin: 0 5px !important;
          }
          
          .file-upload-area {
            padding: 30px 15px !important;
          }
          
          .question-card {
            margin-bottom: 15px !important;
          }
          
          .form-control-lg, .form-select-lg {
            font-size: 16px; /* Prevents zoom on iOS */
          }
          
          h1 {
            font-size: 24px !important;
          }
          
          h3 {
            font-size: 20px !important;
          }
          
          .alert {
            font-size: 14px;
          }
        }

        /* Touch-friendly improvements */
        @media (hover: none) and (pointer: coarse) {
          .btn {
            padding: 14px 20px;
            font-size: 16px;
          }
          
          .question-card:hover {
            transform: none;
          }
        }
      `}</style>

      <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
        <div className="container py-4">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="d-flex justify-content-center align-items-center mb-3">
              <Sparkles className="text-warning me-2 float-animation" size={isMobile ? 24 : 32} />
              <h1 className="text-white mb-0 fw-bold" style={{ fontSize: isMobile ? '24px' : '32px' }}>
                Resume AI Improver
              </h1>
              <Sparkles className="text-warning ms-2 float-animation" size={isMobile ? 24 : 32} />
            </div>
            <p className="text-white-50" style={{ fontSize: isMobile ? '16px' : '18px' }}>
              Transform your resume with AI-powered enhancement
            </p>
            
            {/* Indicators */}
            {isMobile && (
              <div className="alert alert-light d-inline-block mb-2 py-1 px-2">
                <Smartphone className="me-1" size={14} />
                <small>Mobile Optimized</small>
              </div>
            )}
            
            {IS_DEV && (
              <div className="alert alert-info d-inline-block mb-2">
                <small>🔧 Development Mode - v{APP_VERSION}</small>
              </div>
            )}
            
            {ENABLE_DEMO_MODE && (
              <div className="alert alert-secondary d-inline-block mb-2">
                <small>🎭 Demo Mode Enabled</small>
              </div>
            )}
            
            {apiError && (
              <div className={`alert ${IS_DEV || ENABLE_DEMO_MODE ? 'alert-warning' : 'alert-danger'} d-inline-block`}>
                <AlertCircle className="me-2" size={16} />
                {IS_DEV || ENABLE_DEMO_MODE ? 'Demo Mode' : 'Service Error'}: {apiError}
              </div>
            )}
          </div>

          {/* Progress Steps */}
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-center">
                <div className="step-indicator d-flex align-items-center">
                  {[1, 2, 3, 4].map((step) => (
                    <React.Fragment key={step}>
                      <div 
                        className={`step-circle d-flex align-items-center justify-content-center rounded-circle fw-bold transition-all ${
                          currentStep > step ? 'bg-success text-white checkmark' : 
                          currentStep === step ? 'bg-primary text-white' : 
                          'bg-white text-secondary'
                        }`}
                        style={{ 
                          width: isMobile ? '40px' : '50px', 
                          height: isMobile ? '40px' : '50px', 
                          fontSize: isMobile ? '14px' : '16px',
                          transition: 'all 0.3s ease',
                          cursor: step < currentStep ? 'pointer' : 'default'
                        }}
                        onClick={() => step < currentStep && setCurrentStep(step)}
                      >
                        {currentStep > step ? <CheckCircle size={isMobile ? 20 : 24} /> : step}
                      </div>
                      {step < 4 && (
                        <div 
                          className={`step-line ${currentStep > step ? 'bg-success' : currentStep === step ? 'bg-primary' : 'bg-secondary'}`}
                          style={{ 
                            width: isMobile ? '30px' : '80px', 
                            height: '4px', 
                            transition: 'all 0.3s ease',
                            margin: isMobile ? '0 5px' : '0 10px' 
                          }}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </div>
              <div className="d-flex justify-content-center mt-3">
                <div className="text-center text-white-50 small">
                  <div style={{ width: isMobile ? '200px' : '300px' }}>
                    {['Upload Resume', 'Answer Questions', 'AI Processing', 'Download Result'][currentStep - 1]}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="row justify-content-center">
            <div className={`col-lg-8 ${isMobile ? 'col-12' : ''}`}>
              <div className="card shadow-lg border-0" style={{ borderRadius: '20px' }}>
                <div className="card-body" style={{ padding: isMobile ? '20px' : '40px' }}>
                  
                  {/* Step 1: File Upload */}
                  {currentStep === 1 && (
                    <div className="text-center">
                      <Upload className="text-primary mb-4 float-animation" size={isMobile ? 48 : 64} />
                      <h3 className="mb-4" style={{ fontSize: isMobile ? '20px' : '24px' }}>Upload Your Resume</h3>
                      <p className="text-muted mb-4">Upload your current resume in PDF format to get started</p>
                      
                      <div 
                        className={`file-upload-area mb-4 ${isDragOver ? 'dragover' : ''}`}
                        onDragOver={handleDragOver}
                        onDragLeave={handleDragLeave}
                        onDrop={handleDrop}
                        onClick={() => document.getElementById('file-input').click()}
                        style={{ padding: isMobile ? '30px 15px' : '40px 20px' }}
                      >
                        <input
                          id="file-input"
                          type="file"
                          accept=".pdf"
                          onChange={handleFileChange}
                          className="d-none"
                        />
                        
                        {!pdfFile ? (
                          <div>
                            <Upload className="text-primary mb-3" size={isMobile ? 36 : 48} />
                            <h5 style={{ fontSize: isMobile ? '16px' : '18px' }}>
                              {isMobile ? 'Tap to upload' : 'Click to upload or drag and drop'}
                            </h5>
                            <p className="text-muted mb-0">PDF files only (max {Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB)</p>
                          </div>
                        ) : (
                          <div className={`d-flex align-items-center ${isMobile ? 'flex-column text-center' : 'justify-content-center'}`}>
                            <FileText className="text-success me-3" size={32} />
                            <div className={isMobile ? 'mt-2' : ''}>
                              <h6 className="mb-0 text-success">{pdfFile.name}</h6>
                              <small className="text-muted">{(pdfFile.size / 1024 / 1024).toFixed(2)} MB</small>
                            </div>
                            <button 
                              className={`btn btn-outline-danger btn-sm ${isMobile ? 'mt-2' : 'ms-3'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setPdfFile(null);
                              }}
                            >
                              <X size={16} />
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <button
                        className="btn btn-primary btn-lg px-5"
                        style={{ borderRadius: '25px' }}
                        onClick={nextStep}
                        disabled={!pdfFile}
                      >
                        Continue <ArrowRight className="ms-2" size={20} />
                      </button>
                    </div>
                  )}

                  {/* Step 2: Questions */}
                  {currentStep === 2 && (
                    <div>
                      <h3 className="text-center mb-4" style={{ fontSize: isMobile ? '20px' : '24px' }}>
                        Tell Us About Your Goals
                      </h3>
                      <p className="text-center text-muted mb-4">Help us customize your resume improvement</p>
                      
                      <div className="row">
                        {questions.map((question, index) => (
                          <div key={question.id} className="col-12 mb-3">
                            <div 
                              className={`question-card p-3 border rounded-3 h-100 ${errors[question.id] ? 'border-danger' : ''}`}
                              style={{ 
                                background: 'linear-gradient(45deg, #f8f9fa, #ffffff)',
                                border: errors[question.id] ? '2px solid #dc3545' : '2px solid #e9ecef',
                                transition: 'all 0.3s ease',
                                animation: `slideIn 0.5s ease-out ${index * 0.05}s both`
                              }}
                            >
                              <label className="form-label fw-bold d-flex align-items-center" style={{ fontSize: isMobile ? '14px' : '16px' }}>
                                <span className="me-2" style={{ fontSize: '1.2em' }}>{question.icon}</span>
                                {question.question}
                                {question.required && <span className="text-danger ms-1">*</span>}
                              </label>
                              
                              {question.type === 'text' && (
                                <input
                                  type="text"
                                  className={`form-control form-control-lg form-control-enhanced ${errors[question.id] ? 'is-invalid' : ''}`}
                                  placeholder={question.placeholder}
                                  value={formData[question.id] || ''}
                                  onChange={(e) => handleInputChange(question.id, e.target.value)}
                                  style={{ fontSize: '16px' }}
                                />
                              )}
                              
                              {question.type === 'select' && (
                                <select
                                  className={`form-select form-select-lg form-control-enhanced ${errors[question.id] ? 'is-invalid' : ''}`}
                                  value={formData[question.id] || ''}
                                  onChange={(e) => handleInputChange(question.id, e.target.value)}
                                  style={{ fontSize: '16px' }}
                                >
                                  <option value="">Select an option</option>
                                  {question.options.map(option => (
                                    <option key={option} value={option}>{option}</option>
                                  ))}
                                </select>
                              )}
                              
                              {question.type === 'textarea' && (
                                <textarea
                                  className={`form-control form-control-lg form-control-enhanced ${errors[question.id] ? 'is-invalid' : ''}`}
                                  rows="3"
                                  placeholder={question.placeholder}
                                  value={formData[question.id] || ''}
                                  onChange={(e) => handleInputChange(question.id, e.target.value)}
                                  style={{ fontSize: '16px' }}
                                />
                              )}
                              
                              {errors[question.id] && (
                                <div className="invalid-feedback d-block">
                                  {errors[question.id]}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <div className={`text-center mt-4 ${isMobile ? 'd-grid gap-2' : ''}`}>
                        <button
                          className={`btn btn-outline-secondary btn-lg px-4 ${isMobile ? 'mb-2' : 'me-3'}`}
                          style={{ borderRadius: '25px' }}
                          onClick={prevStep}
                        >
                          <ArrowLeft className="me-2" size={20} /> Back
                        </button>
                        <button
                          className="btn btn-gradient btn-lg px-5"
                          style={{ borderRadius: '25px' }}
                          onClick={handleSubmit}
                        >
                          Improve My Resume <Sparkles className="ms-2" size={20} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Processing */}
                  {currentStep === 3 && (
                    <div className="text-center">
                      <div className="mb-4">
                        <div className="spinner-custom mx-auto" />
                      </div>
                      <h3 className="mb-4" style={{ fontSize: isMobile ? '20px' : '24px' }}>
                        AI is Improving Your Resume
                      </h3>
                      <p className="text-muted mb-4">{progressText}</p>
                      
                      <div className="progress progress-enhanced mb-4" style={{ height: '20px' }}>
                        <div
                          className="progress-bar progress-bar-custom progress-bar-striped progress-bar-animated"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      
                      <div className="small text-muted mb-4">
                        {progress}% Complete
                      </div>
                      
                      {apiError && (
                        <div className="alert alert-info">
                          <AlertCircle className="me-2" size={16} />
                          {apiError.includes('Demo Mode') ? 
                            'Running in demo mode - generating sample improved resume...' :
                            'Processing your resume...'
                          }
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 4: Results */}
                  {currentStep === 4 && result && (
                    <div>
                      <div className="text-center mb-4">
                        <CheckCircle className="text-success mb-3 checkmark" size={isMobile ? 48 : 64} />
                        <h3 className="text-success mb-2" style={{ fontSize: isMobile ? '20px' : '24px' }}>
                          Resume Successfully Improved!
                        </h3>
                        <p className="text-muted">
                          Your resume has been enhanced with AI-powered improvements
                          {result.format === 'pdf' ? ' and professionally formatted as a PDF' : ''}
                        </p>
                      </div>
                      
                      <div className="row mb-4">
                        <div className="col-md-4 mb-3">
                          <div className="card bg-primary text-white text-center h-100">
                            <div className="card-body">
                              <h5>Format</h5>
                              <h3>{result.format === 'pdf' || result.improved_resume_pdf ? '📄 PDF' : '📝 TEXT'}</h3>
                              <small>{result.format === 'pdf' || result.improved_resume_pdf ? 'LaTeX compiled' : 'Plain text'}</small>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="card bg-success text-white text-center h-100">
                            <div className="card-body">
                              <h5>Action Verbs</h5>
                              <h3>+{result.analytics?.improvements?.action_verb_increase || 0}</h3>
                              <small>enhanced</small>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-4 mb-3">
                          <div className="card bg-info text-white text-center h-100">
                            <div className="card-body">
                              <h5>Quantified Results</h5>
                              <h3>+{result.analytics?.improvements?.quantification_increase || 0}</h3>
                              <small>added</small>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Preview Section */}
                      <div className="card mb-4">
                        <div className="card-header d-flex justify-content-between align-items-center">
                          <h6 className="mb-0">
                            Preview - {result.improved_resume_pdf ? 'PDF Resume' : 'Text Resume'}
                          </h6>
                          <div>
                            {result.improved_resume_pdf ? (
                              <button
                                className="btn btn-outline-success btn-sm"
                                onClick={() => {
                                  const pdfBlob = base64ToBlob(result.improved_resume_pdf, 'application/pdf');
                                  if (pdfBlob) {
                                    const url = window.URL.createObjectURL(pdfBlob);
                                    window.open(url, '_blank');
                                    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
                                  }
                                }}
                              >
                                <Eye size={16} className="me-1" />
                                View PDF
                              </button>
                            ) : (
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => setShowPreviewModal(true)}
                              >
                                <Eye size={16} className="me-1" />
                                Full View
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="card-body">
                          {result.improved_resume_pdf ? (
                            <div className="text-center p-4">
                              <FileText className="text-success mb-3" size={48} />
                              <h5>Professional PDF Resume Ready!</h5>
                              <p className="text-muted">
                                Your resume has been professionally formatted using LaTeX and compiled to PDF.
                                Click "View PDF" above to preview.
                              </p>
                              <div className="badge bg-success">
                                LaTeX Compiled • ATS Optimized • Print Ready
                              </div>
                            </div>
                          ) : (
                            <pre className="mb-0 small" style={{ fontSize: isMobile ? '12px' : '14px' }}>
                              {(result.improved_resume || result.improved_resume_text || '').substring(0, isMobile ? 300 : 500)}...
                            </pre>
                          )}
                        </div>
                      </div>
                      
                      <div className={`text-center ${isMobile ? 'd-grid gap-2' : ''}`}>
                        <button
                          className={`btn btn-success btn-lg px-5 ${isMobile ? 'mb-2' : 'me-3'}`}
                          style={{ borderRadius: '25px' }}
                          onClick={handleDownload}
                        >
                          <Mail className="me-2" size={20} />
                          Get Resume via Email
                        </button>
                        <button
                          className="btn btn-outline-primary btn-lg px-4"
                          style={{ borderRadius: '25px' }}
                          onClick={resetApp}
                        >
                          <RefreshCw className="me-2" size={16} />
                          Start Over
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Email Modal */}
        {showEmailModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center">
                    <Mail className="me-2" size={24} />
                    Enter Your Email Address
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailError('');
                    }}
                  />
                </div>
                <div className="modal-body">
                  <div className="alert alert-info mb-3">
                    <Shield className="me-2" size={16} />
                    We'll send a verification code to your email before delivering your resume.
                  </div>
                  <div className="mb-3">
                    <input
                      type="email"
                      className={`form-control form-control-lg ${emailError ? 'is-invalid' : ''}`}
                      placeholder="your.email@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendOtpToEmail()}
                      style={{ fontSize: '16px' }}
                    />
                    {emailError && <div className="invalid-feedback">{emailError}</div>}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailError('');
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary px-4"
                    onClick={sendOtpToEmail}
                    disabled={isOtpLoading}
                  >
                    {isOtpLoading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="me-2" size={16} />
                        Send Verification Code
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* OTP Modal */}
        {showOtpModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title d-flex align-items-center">
                    <Shield className="me-2" size={24} />
                    Enter Verification Code
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowOtpModal(false);
                      setOtpError('');
                      setOtpCode('');
                    }}
                  />
                </div>
                <div className="modal-body text-center">
                  <div className="alert alert-success mb-3">
                    <CheckCircle className="me-2" size={16} />
                    Verification code sent to <strong>{email}</strong>
                  </div>
                  
                  <p className="text-muted mb-4">
                    Please enter the 6-digit verification code sent to your email.
                  </p>
                  
                  <div className="mb-3">
                    <input
                      type="text"
                      className={`form-control form-control-lg text-center ${otpError ? 'is-invalid' : ''}`}
                      placeholder="000000"
                      value={otpCode}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                        setOtpCode(value);
                        if (otpError) setOtpError('');
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && verifyOtpAndSendEmail()}
                      maxLength="6"
                      style={{ fontSize: '24px', letterSpacing: '8px', fontFamily: 'monospace' }}
                    />
                    {otpError && <div className="invalid-feedback">{otpError}</div>}
                  </div>
                  
                  <div className="mb-3">
                    {otpResendCountdown > 0 ? (
                      <div className="text-muted">
                        Resend code in {otpResendCountdown}s
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-link btn-sm"
                        onClick={sendOtpToEmail}
                        disabled={isOtpLoading}
                      >
                        {isOtpLoading ? 'Sending...' : 'Resend verification code'}
                      </button>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowOtpModal(false);
                      setShowEmailModal(true);
                      setOtpError('');
                      setOtpCode('');
                    }}
                  >
                    <ArrowLeft className="me-2" size={16} />
                    Change Email
                  </button>
                  <button
                    type="button"
                    className="btn btn-success px-4"
                    onClick={verifyOtpAndSendEmail}
                    disabled={isVerifyingOtp || otpCode.length !== 6}
                  >
                    {isVerifyingOtp ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" />
                        Verifying...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="me-2" size={16} />
                        Verify & Send Resume
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Preview Modal*/}
        {showPreviewModal && (
          <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className={`modal-dialog ${isMobile ? 'modal-fullscreen' : 'modal-lg modal-dialog-centered'}`}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <FileText className="me-2" size={24} />
                    Improved Resume - Full Preview
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowPreviewModal(false)}
                  />
                </div>
                <div className="modal-body" style={{ maxHeight: isMobile ? 'none' : '70vh', overflow: 'auto' }}>
                  <pre className="mb-0" style={{ 
                    whiteSpace: 'pre-wrap', 
                    fontSize: isMobile ? '12px' : '14px',
                    lineHeight: '1.4'
                  }}>
                    {result?.improved_resume || result?.improved_resume_text || ''}
                  </pre>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowPreviewModal(false)}
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowPreviewModal(false);
                      handleDownload();
                    }}
                  >
                    <Download className="me-2" size={16} />
                    Get via Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default ResumeImproverApp;